import { useEffect, useRef, useState } from 'react'

import type { PageEventType, TrackEventType } from 'utils/rudderstack'
import { useVisitorEventTracking } from 'utils/rudderstack/useVisitorEventTracking'

type VisitorEvent =
  | TrackEventType.START_LISTENING_BUTTON_CLICKED
  | TrackEventType.PLAY_BUTTON_CLICKED
  | TrackEventType.MUTE_BUTTON_CLICKED
  | TrackEventType.AUDIO_STARTED_PLAYBACK
  | TrackEventType.TRANSCRIPT_OPENED
  | TrackEventType.TRANSCRIPT_CLOSED
  | TrackEventType.INFO_MODAL_OPENED
  | TrackEventType.INFO_MODAL_CLOSED

type UseDebouncedTrackingProps = {
  pageName: PageEventType | null
  isShowing: boolean
  openedEvent: VisitorEvent
  closedEvent: VisitorEvent
  debounceMs?: number
}

/**
 * Simple hook to debounce tracking of a modal opening and closing.
 *
 * We have a similar one already but this takes two events to track
 * and is more flexible. Takes a opening and a closing event to track.
 */
export const useDebouncedTracking = ({
  pageName,
  isShowing,
  openedEvent,
  closedEvent,
  debounceMs = 300,
}: UseDebouncedTrackingProps): void => {
  const rudderstackTrack = useVisitorEventTracking({
    pageName,
  })

  const [hasInitialized, setHasInitialized] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const previousShowState = useRef(isShowing)

  useEffect(() => {
    // Skip tracking on initial load when modal is closed
    if (!hasInitialized) {
      setHasInitialized(true)
      previousShowState.current = isShowing
      return
    }

    // Only track if the state actually changed
    if (previousShowState.current !== isShowing) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Debounce the tracking
      timeoutRef.current = setTimeout(() => {
        if (isShowing) {
          rudderstackTrack({
            event: openedEvent,
          })
        } else {
          rudderstackTrack({
            event: closedEvent,
          })
        }
      }, debounceMs)

      previousShowState.current = isShowing
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    isShowing,
    hasInitialized,
    rudderstackTrack,
    openedEvent,
    closedEvent,
    debounceMs,
  ])
}
